import { AxiosInstance } from 'axios';

import {
  GetApiV5MessagesParams,
  GetApiV5MessagesTab,
  PatchApiV5MessagesIdBody,
  PostApiV5MessagesBody,
  PostApiV5MessagesIdSendBody,
  PostApiV5MessagesIdSendBodyMessages,
  PostApiV5MessagesIdSendBodyParticipantsItem,
  PatchApiV5MessagesIdParticipantsBody,
  PatchApiV5MessagesIdParticipantsBodyParticipantsItem,
  PostApiV5MessagesBulkArchiveBody,
  GetApiV5Messages200,
  GetApiV5MessagesCounts200,
  GetApiV5MessagesThreadsThreadId200,
  PostApiV5Messages201,
  PostApiV5MessagesIdSend200,
  PatchApiV5MessagesIdParticipants200,
  PostApiV5MessagesThreadsIdArchive200,
  PostApiV5MessagesBulkArchive200,
  PatchApiV5MessagesIdMarkUnread200,
  PatchApiV5MessagesIdPin200,
  PatchApiV5MessagesThreadsThreadIdPin200,
  DeleteApiV5MessagesIdDiscard200,
  DeleteApiV5MessagesId200,
  MessagePostBody,
  MessagesResponse,
  MessagesOfContactResponse,
  ThreadResponse,
  MessagesSearchArgs,
  DraftOrThreadResponse,
  DiscardMessageDraftResponse,
  UpdateMessageUnreadStatusPostBody,
  CreateUserViewPostBody,
  UpdateUserViewPostBody,
  Participant,
  Role,
  toMessageApiError,
} from '@liscio/api';
import { normalizeMessageSearchKeyword } from '@liscio/utils';

type SendParticipantType = PostApiV5MessagesIdSendBodyParticipantsItem['type'];

interface NormalizedParticipant {
  id: number;
  type: Extract<SendParticipantType, 'contact' | 'user'>;
  is_recipient: boolean;
  account_id?: number;
  message_participant_id?: number;
}

function normalizeParticipant(participant: Participant): NormalizedParticipant {
  const isUninvitedContact =
    participant.type === Role.Contact && participant.cpa_user_id == null;
  const membershipId =
    participant.cpa_user_id ??
    (participant.type === Role.User ? participant.id : undefined);
  const rawId = isUninvitedContact
    ? participant.contact_id ?? participant.id
    : membershipId;
  const id = Number(rawId);

  if (!Number.isInteger(id) || id <= 0) {
    const error = new Error('One or more recipients are unavailable.');
    error.name = 'recipient_unavailable';
    throw error;
  }

  const accountId = Number(participant.account_id);
  const messageParticipantId = Number(participant.message_participant_id);

  return {
    id,
    type: isUninvitedContact ? 'contact' : 'user',
    is_recipient: participant.is_recipient !== false,
    ...(Number.isInteger(accountId) && accountId > 0
      ? { account_id: accountId }
      : {}),
    ...(Number.isInteger(messageParticipantId) && messageParticipantId > 0
      ? { message_participant_id: messageParticipantId }
      : {}),
  };
}

function normalizeParticipants(
  participants: Participant[]
): NormalizedParticipant[] {
  const normalizedParticipants = new Map<string, NormalizedParticipant>();

  participants.forEach((participant) => {
    const normalized = normalizeParticipant(participant);
    // Recipient role is part of the backend participant persistence key.
    const key = `${normalized.type}:${normalized.id}:${
      normalized.account_id ?? 0
    }:${normalized.is_recipient}`;
    if (!normalizedParticipants.has(key)) {
      normalizedParticipants.set(key, normalized);
    }
  });

  return Array.from(normalizedParticipants.values());
}

function mapParticipantForUpdate(
  participant: NormalizedParticipant
): PatchApiV5MessagesIdParticipantsBodyParticipantsItem {
  return {
    id: participant.id,
    type: 'user',
    is_recipient: participant.is_recipient,
    ...(participant.account_id ? { account_id: participant.account_id } : {}),
    ...(participant.message_participant_id
      ? { message_participant_id: participant.message_participant_id }
      : {}),
  };
}

function membershipParticipantsForDraft(
  participants: Participant[]
): PatchApiV5MessagesIdParticipantsBodyParticipantsItem[] {
  return normalizeParticipants(participants)
    .filter((participant) => participant.type === 'user')
    .map(mapParticipantForUpdate);
}

function mapParticipantForSend(
  participant: NormalizedParticipant
): PostApiV5MessagesIdSendBodyParticipantsItem {
  return {
    id: participant.id,
    type: participant.type,
    is_recipient: participant.is_recipient,
    ...(participant.type === 'user' ? { cpa_user_id: participant.id } : {}),
    ...(participant.account_id ? { account_id: participant.account_id } : {}),
  };
}

/**
 * V5 Messages API adapter that wraps the generated orval V5 API functions
 * to match the V3 messages API interface, allowing for seamless switching
 * via the useMessagesApi hook.
 */
export default function createV5MessagesCalls(client: AxiosInstance) {
  /**
   * Get messages with filters and pagination
   * Maps V3 getMessages to V5 GET /api/v5/messages
   */
  async function getMessages({
    type,
    pageParam,
    searchString,
    perPage = 15,
    accountId,
    user_view_id,
    recipient_id,
    from_date,
    to_date,
    attachment,
    includeCounts = false,
  }: MessagesSearchArgs & { includeCounts?: boolean }): Promise<
    MessagesResponse['data']
  > {
    const normalizedSearch = normalizeMessageSearchKeyword(searchString);
    if (!normalizedSearch.isValid) {
      throw new Error('Keyword must be at least 3 characters.');
    }

    const params: GetApiV5MessagesParams = {
      tab: type as GetApiV5MessagesTab,
      page: pageParam || 1,
      per_page: perPage,
      include_counts: includeCounts,
      ...(normalizedSearch.keyword !== undefined && {
        keyword: normalizedSearch.keyword,
      }),
      ...(user_view_id && { user_view_id }),
      ...(accountId && { account_id: accountId }),
      ...(recipient_id && { recipient_id }),
      ...(from_date && { from_date }),
      ...(to_date && { to_date }),
      ...(attachment !== undefined && { attachment }),
    };

    const response = await client.get<GetApiV5Messages200>('/api/v5/messages', {
      params,
    });

    const data = response.data.data;
    const responseKey = type === 'all' ? 'all_messages' : type;
    const activeMessages = data?.[responseKey as keyof typeof data];
    const fallbackHasNextPage =
      Array.isArray(activeMessages) && activeMessages.length === perPage;
    // V5 API returns optional arrays, but V3 interface expects required arrays
    // We normalize by providing empty arrays as defaults
    // Note: Using 'as unknown as' because V5MessageListItem and Message types don't overlap
    return {
      ...data,
      inbox: data?.inbox || [],
      sent: data?.sent || [],
      draft: data?.draft || [],
      archive: data?.archive || [],
      alert: data?.alert || [],
      pinned: data?.pinned || [],
      all_messages: data?.all_messages || [],
      has_next_page: data?.has_next_page ?? fallbackHasNextPage,
    } as unknown as MessagesResponse['data'];
  }

  /**
   * Wrapper for getMessages used by react-query
   */
  async function getMessagesQueryKeyWrapper({
    pageParam = 1,
    queryKey = [],
  }: {
    pageParam?: number | null;
    queryKey: Array<string>;
  }): Promise<MessagesResponse['data']> {
    const [, type, searchString] = queryKey;
    return getMessages({ type, pageParam, searchString, includeCounts: false });
  }

  /**
   * Get messages of a specific contact
   * Maps V3 getMessagesOfContact to V5 GET /api/v5/messages with recipient filter
   */
  async function getMessagesOfContact({
    pageParam = 1,
    queryKey = [],
  }: {
    pageParam?: number | null;
    queryKey: Array<string>;
  }): Promise<MessagesOfContactResponse['data'] | void> {
    const [, contactId] = queryKey;

    try {
      const params: GetApiV5MessagesParams = {
        page: pageParam || 1,
        per_page: 15,
        include_counts: false,
        recipient_id: parseInt(contactId, 10),
      };

      const response = await client.get<GetApiV5Messages200>(
        '/api/v5/messages',
        { params }
      );

      // Transform V5 response to match V3 structure
      // V5 API returns optional inbox array, but V3 interface expects required array
      // Note: Using 'as unknown as' because V5MessageListItem and BasicMessage types don't overlap
      const data = response.data.data;
      return {
        ...data,
        inbox: data?.inbox || [],
        has_next_page: data?.has_next_page ?? (data?.inbox?.length ?? 0) === 15,
      } as unknown as MessagesOfContactResponse['data'];
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  /**
   * Get thread messages
   * Maps V3 /api/v3/get_thread_messages/:id to V5 GET /api/v5/messages/threads/:thread_id
   */
  async function getMessageThread({
    queryKey = [],
  }: {
    queryKey: Array<string>;
  }): Promise<ThreadResponse | undefined> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_key, threadId] = queryKey;

    // Don't make API call if threadId is missing or empty
    if (!threadId || threadId === 'undefined' || threadId === 'null') {
      return undefined;
    }

    const url = `/api/v5/messages/threads/${encodeURIComponent(threadId)}`;

    const response = await client.get<GetApiV5MessagesThreadsThreadId200>(url);

    if (!response.data.success) {
      throw new Error('Unable to read messages');
    }

    return response.data as ThreadResponse;
  }

  /**
   * Create or update a draft message
   * Maps V3 createDraft to V5 POST /api/v5/messages
   */
  async function createDraft(
    message: MessagePostBody
  ): Promise<DraftOrThreadResponse> {
    const body: PostApiV5MessagesBody = {
      thread: {
        title: message.thread?.title || '',
        status: message.thread?.status,
        is_notify_via_text: message.thread?.is_notify_via_text,
      },
      messages: {
        content: message.messages.content,
        status: message.messages.status,
        message_thread_id: message.messages.message_thread_id,
      },
      doc_ids: message.doc_ids || [],
      attachment_origins: message.attachment_origins,
    };

    const response = await client.post<PostApiV5Messages201>(
      '/api/v5/messages',
      body
    );

    return response.data as DraftOrThreadResponse;
  }

  /**
   * Send a message
   * Maps V3 sendMessage to V5 POST /api/v5/messages/:id/send
   */
  async function sendMessage(
    message: MessagePostBody
  ): Promise<DraftOrThreadResponse> {
    const messageId = message.id;

    if (!messageId) {
      throw new Error('Message ID is required to send a message');
    }

    try {
      const messagesObject: PostApiV5MessagesIdSendBodyMessages = {
        content: message.messages.content,
      };

      if (message.messages.ai_message_draft_uuid) {
        messagesObject.ai_message_draft_uuid =
          message.messages.ai_message_draft_uuid;
      }

      const body: PostApiV5MessagesIdSendBody = {
        thread: {
          title: message.thread?.title || '',
          is_notify_via_text: message.thread?.is_notify_via_text,
        },
        messages: messagesObject,
        participants: normalizeParticipants(message.participants || []).map(
          mapParticipantForSend
        ),
        private_documents_acknowledged: message.private_documents_acknowledged,
      };
      const response = await client.post<PostApiV5MessagesIdSend200>(
        `/api/v5/messages/${encodeURIComponent(messageId)}/send`,
        body
      );

      return response.data as DraftOrThreadResponse;
    } catch (error) {
      throw toMessageApiError(
        error,
        'Failed to send message. Please try again.'
      );
    }
  }

  /**
   * Update message participants
   * Maps V3 updateParticipants to V5 PATCH /api/v5/messages/:id/participants
   */
  async function updateParticipants(
    messageId: string,
    participants: Participant[]
  ): Promise<PatchApiV5MessagesIdParticipants200> {
    try {
      const body: PatchApiV5MessagesIdParticipantsBody = {
        participants: membershipParticipantsForDraft(participants),
      };
      const response = await client.patch<PatchApiV5MessagesIdParticipants200>(
        `/api/v5/messages/${encodeURIComponent(messageId)}/participants`,
        body
      );

      return response.data;
    } catch (error) {
      throw toMessageApiError(
        error,
        'Failed to update recipients. Please try again.'
      );
    }
  }

  /**
   * Archive a thread
   * Maps V3 archiveThread to V5 POST /api/v5/messages/threads/:id/archive
   */
  async function archiveThread(threadId: string) {
    const response = await client.post<PostApiV5MessagesThreadsIdArchive200>(
      `/api/v5/messages/threads/${encodeURIComponent(threadId)}/archive`,
      {}
    );

    return response.data;
  }

  /**
   * Bulk archive threads
   * Maps V3 bulkArchiveThreads to V5 POST /api/v5/messages/bulk/archive
   */
  async function bulkArchiveThreads(threadIds: string[]) {
    const body: PostApiV5MessagesBulkArchiveBody = {
      ids: threadIds,
    };

    const response = await client.post<PostApiV5MessagesBulkArchive200>(
      '/api/v5/messages/bulk/archive',
      body
    );

    return response.data;
  }

  /**
   * Update message read/unread status
   * Maps V3 updateMessageUnreadStatus to V5 PATCH /api/v5/messages/:id/mark_unread
   */
  async function updateMessageUnreadStatus(
    body: UpdateMessageUnreadStatusPostBody
  ) {
    const messageId = body.message_id;

    if (!messageId) {
      throw new Error('Message ID is required');
    }

    // V5 only has mark_unread endpoint
    // Skip the call if message should be read
    const isRead = 'is_read' in body ? body.is_read : false;
    if (!isRead) {
      const response = await client.patch<PatchApiV5MessagesIdMarkUnread200>(
        `/api/v5/messages/${encodeURIComponent(messageId)}/mark_unread`,
        {}
      );

      return response.data;
    }

    return { success: true };
  }

  /**
   * Toggle message pin status
   * Maps V3 toggleMessageIsPinned to V5 PATCH /api/v5/messages/:id/pin
   */
  async function toggleMessageIsPinned(messageId: string) {
    const response = await client.patch<PatchApiV5MessagesIdPin200>(
      `/api/v5/messages/${encodeURIComponent(messageId)}/pin`,
      {}
    );

    return response.data;
  }

  /**
   * Toggle thread pin status
   * Maps V3 toggleThreadIsPinned to V5 PATCH /api/v5/messages/threads/:thread_id/pin
   */
  async function toggleThreadIsPinned(threadId: string) {
    const response =
      await client.patch<PatchApiV5MessagesThreadsThreadIdPin200>(
        `/api/v5/messages/threads/${encodeURIComponent(threadId)}/pin`,
        {}
      );

    return response.data;
  }

  /**
   * Discard a draft message
   * Maps V3 discardMessageDraft to V5 DELETE /api/v5/messages/:id/discard
   */
  async function discardMessageDraft(
    messageId: string
  ): Promise<DiscardMessageDraftResponse> {
    const response = await client.delete<DeleteApiV5MessagesIdDiscard200>(
      `/api/v5/messages/${encodeURIComponent(messageId)}/discard`
    );

    return response.data as DiscardMessageDraftResponse;
  }

  /**
   * Delete a message
   * Maps V3 deleteMessage to V5 DELETE /api/v5/messages/:id
   */
  async function deleteMessage(messageId: string) {
    const response = await client.delete<DeleteApiV5MessagesId200>(
      `/api/v5/messages/${encodeURIComponent(messageId)}`
    );

    return response.data;
  }

  /**
   * UserView methods - these still use V3 endpoints as they weren't migrated to V5
   */
  async function getAllUserViews(viewId?: string) {
    const params = viewId ? `?view_id=${encodeURIComponent(viewId)}` : '';
    const response = await client.get(`/api/v3/get_all_messages_view${params}`);
    return response.data;
  }

  async function getUserViewDetails(viewId: string) {
    const response = await client.get(
      `/api/v3/user_views/${encodeURIComponent(viewId)}`
    );
    return response.data.data;
  }

  async function createUserView(viewData: CreateUserViewPostBody) {
    const response = await client.post('/api/v3/user_views', viewData);
    return response.data;
  }

  async function updateUserView(
    viewId: string,
    viewData: UpdateUserViewPostBody
  ) {
    const response = await client.put(
      `/api/v3/user_views/${encodeURIComponent(viewId)}`,
      viewData
    );
    return response.data;
  }

  async function deleteUserView(viewId: string) {
    const response = await client.delete(
      `/api/v3/user_views/${encodeURIComponent(viewId)}`
    );
    return response.data;
  }

  /**
   * Send user email - V3 endpoint (not migrated to V5)
   */
  async function sendUserEmail(body: Record<string, unknown>) {
    const response = await client.post('/api/v3/send_user_email', body);
    return response.data;
  }

  /**
   * Get message by ID
   */
  async function getMessage(messageId: string) {
    const response = await client.get(
      `/api/v5/messages/${encodeURIComponent(messageId)}`
    );
    return response.data;
  }

  /**
   * Update draft message
   * Maps V3 updateDraft to V5 PATCH /api/v5/messages/:id
   */
  async function updateDraft(message: Omit<MessagePostBody, 'participants'>) {
    try {
      const body: PatchApiV5MessagesIdBody = {
        messages: {
          content: message.messages.content,
          message_thread_id: message.messages.message_thread_id,
          status: message.messages.status,
        },
        thread: {
          title: message.thread?.title || '',
          status: message.thread?.status,
          is_notify_via_text: message.thread?.is_notify_via_text,
        },
        doc_ids: message.doc_ids || [],
        attachment_origins: message.attachment_origins,
      };

      const response = await client.patch(
        `/api/v5/messages/${encodeURIComponent(message.id)}`,
        body
      );

      return response.data as DraftOrThreadResponse;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Update participants in a draft message
   * Maps V3 updateParticipantsInDraft to V5 PATCH /api/v5/messages/:id/participants
   */
  async function updateParticipantsInDraft(
    body: Pick<MessagePostBody, 'participants' | 'id'> & {
      account_ids?: number[];
    }
  ) {
    try {
      const participantsBody: PatchApiV5MessagesIdParticipantsBody = {
        ...(body.account_ids ? { account_ids: body.account_ids } : {}),
        participants: membershipParticipantsForDraft(body.participants || []),
      };

      type UpdateParticipantsResponse = Omit<
        PatchApiV5MessagesIdParticipants200,
        'participants'
      > & {
        participants?: Participant[];
        message_thread_id?: string;
      };

      const response = await client.patch<UpdateParticipantsResponse>(
        `/api/v5/messages/${encodeURIComponent(body.id)}/participants`,
        participantsBody
      );

      // Return response in format compatible with DraftOrThreadResponse
      const responseData = response.data;
      return {
        ...responseData,
        message_thread_id: responseData.message_thread_id,
        message_id: responseData.message_id,
      };
    } catch (error) {
      throw toMessageApiError(
        error,
        'Failed to update recipients. Please try again.'
      );
    }
  }

  /**
   * Get message counts for all tabs
   * Maps to V5 GET /api/v5/messages/counts
   * This is a lightweight endpoint that returns only counts without message data
   * Supports all filter params: keyword, account_id, recipient_id, from_date, to_date, attachment, user_view_id
   */
  async function getMessageCounts(params?: {
    keyword?: string;
    account_id?: number;
    recipient_id?: number;
    from_date?: string;
    to_date?: string;
    attachment?: boolean;
    user_view_id?: string;
  }) {
    const normalizedSearch = normalizeMessageSearchKeyword(params?.keyword);
    if (!normalizedSearch.isValid) {
      throw new Error('Keyword must be at least 3 characters.');
    }

    const normalizedParams = { ...params };
    delete normalizedParams.keyword;
    if (normalizedSearch.keyword !== undefined) {
      normalizedParams.keyword = normalizedSearch.keyword;
    }

    const response = await client.get<GetApiV5MessagesCounts200>(
      '/api/v5/messages/counts',
      {
        params: normalizedParams,
      }
    );

    if (!response.data.data) throw new Error('Unable to load message counts');

    return response.data.data;
  }

  return {
    getMessages,
    getMessagesQueryKeyWrapper,
    getMessagesOfContact,
    getMessageThread,
    createDraft,
    sendMessage,
    updateDraft,
    updateParticipantsInDraft,
    getMessage,
    deleteMessage,
    updateParticipants,
    discardMessageDraft,
    archiveThread,
    bulkArchiveThreads,
    toggleMessageIsPinned,
    toggleThreadIsPinned,
    updateMessageUnreadStatus,
    sendUserEmail,
    getAllUserViews,
    getUserViewDetails,
    createUserView,
    updateUserView,
    deleteUserView,
    getMessageCounts,
  };
}
